// The real timeline of our first film, drawn from the Premiere sequence dump
// (public/data/timeline.json: 5,487 clips on 30 tracks, times in tenths of a
// second). Nothing here is illustrative: every block is a clip that was placed.
import { useEffect, useRef, useState } from 'react';

export type Group = 'sub' | 'look' | 'alt' | 'gfx' | 'ai' | 'ft' | 'vo' | 'ji' | 'sfx' | 'mus';

// lane 0..14 = V15..V1, lane 15..29 = A1..A15 (the layout of the sequence)
export const LANE_GROUP: Group[] = [
  'sub', 'look', 'alt', 'alt', 'gfx', 'gfx', 'gfx', 'gfx', 'gfx', 'gfx', 'gfx', 'ai', 'ai', 'look', 'ft',
  'vo', 'ji', 'sfx', 'sfx', 'sfx', 'sfx', 'sfx', 'sfx', 'sfx', 'sfx', 'mus', 'mus', 'mus', 'sfx', 'sfx',
];

export const GROUP_COLOR: Record<Group, string> = {
  sub: '#C9CEDB', look: '#6E7391', alt: '#6672C4', gfx: '#F2A98B', ai: '#B9A2FF', ft: '#7C8CFF',
  vo: '#FF6FB1', ji: '#5BA8F2', sfx: '#5FD3B4', mus: '#3FA98F',
};

export interface TimelineData { end: number; lanes: number[][]; starts: number[]; total: number }

let cache: Promise<TimelineData> | null = null;
export function loadTimeline(): Promise<TimelineData> {
  cache ??= fetch('/data/timeline.json')
    .then((r) => r.json())
    .then((d: { end: number; lanes: number[][] }) => {
      const starts: number[] = [];
      d.lanes.forEach((row) => { for (let k = 0; k < row.length; k += 2) starts.push(row[k]); });
      starts.sort((a, b) => a - b);
      return { ...d, starts, total: starts.length };
    });
  return cache;
}

export function useTimeline() {
  const [data, setData] = useState<TimelineData | null>(null);
  useEffect(() => {
    let live = true;
    void loadTimeline().then((d) => { if (live) setData(d); }).catch(() => {});
    return () => { live = false; };
  }, []);
  return data;
}

// how many clips have started by time t (tenths of a second)
export function clipsBefore(d: TimelineData, t: number) {
  let lo = 0, hi = d.starts.length;
  while (lo < hi) { const m = (lo + hi) >> 1; if (d.starts[m] < t) lo = m + 1; else hi = m; }
  return lo;
}

// how many clips sit in each group (for the legend)
export function groupCounts(d: TimelineData) {
  const c = {} as Record<Group, number>;
  d.lanes.forEach((row, i) => { c[LANE_GROUP[i]] = (c[LANE_GROUP[i]] ?? 0) + row.length / 2; });
  return c;
}

// Draw the window [t0, t1] (tenths of a second) into the canvas. `only` dims
// every lane outside the named groups.
export function drawTimeline(cv: HTMLCanvasElement, d: TimelineData, t0: number, t1: number, only?: Group[] | null) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = cv.clientWidth, h = cv.clientHeight;
  if (!w || !h) return;
  if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) { cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr); }
  const ctx = cv.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
  const split = 5; // the gap between the video and the audio tracks
  const lh = (h - split) / 30;
  const bar = Math.max(1.5, lh - 1.6);
  const span = t1 - t0;
  for (let li = 0; li < 30; li++) {
    const g = LANE_GROUP[li];
    const y = li * lh + (li >= 15 ? split : 0);
    const lit = !only || only.includes(g);
    ctx.fillStyle = 'rgba(255,255,255,0.022)';
    ctx.fillRect(0, y, w, bar);
    ctx.fillStyle = GROUP_COLOR[g];
    ctx.globalAlpha = lit ? 0.92 : 0.1;
    const row = d.lanes[li];
    for (let k = 0; k < row.length; k += 2) {
      const s = row[k], e = s + row[k + 1];
      if (e < t0) continue;
      if (s > t1) break;
      const x = ((s - t0) / span) * w;
      const cw = Math.max(0.7, ((e - s) / span) * w - 0.6);
      ctx.fillRect(x, y, cw, bar);
    }
    ctx.globalAlpha = 1;
  }
  // the hairline between picture and sound
  ctx.fillStyle = 'rgba(255,255,255,0.10)';
  ctx.fillRect(0, 15 * lh + split / 2 - 0.5, w, 1);
}

// A static slice of the real timeline (used inside small scenes).
export function TimelineSlice({ from, to, only, style }: { from: number; to: number; only?: Group[] | null; style?: React.CSSProperties }) {
  const data = useTimeline();
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv || !data) return;
    const paint = () => drawTimeline(cv, data, from * 10, to * 10, only);
    paint();
    const ro = new ResizeObserver(paint);
    ro.observe(cv);
    return () => ro.disconnect();
  }, [data, from, to, only]);
  return <canvas ref={ref} aria-hidden="true" style={{ display: 'block', width: '100%', height: '100%', ...style }} />;
}
